import { useCallback, useEffect, useState } from "react";
import { Box, Typography, Button, Card, Divider, Chip } from "@mui/joy";
import { Plus, WifiOff, FileText, ChevronLeft } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    offlineStore,
    OfflineProject,
    OfflineSyncFailure,
} from "../../utils/offlineStore";
import InitialInspection from "../InitialInspection";
import { useNetworkStatus } from "../../hooks/useNetworkStatus";

export function OfflineDashboard() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const isOnline = useNetworkStatus();
    const [projects, setProjects] = useState<OfflineProject[]>([]);
    const [selectedProject, setSelectedProject] =
        useState<OfflineProject | null>(null);
    const [pending, setPending] = useState({
        projects: 0,
        inspections: 0,
        photos: 0,
    });
    const [failures, setFailures] = useState<OfflineSyncFailure[]>([]);

    const loadData = useCallback(async () => {
        const [p, counts, fail] = await Promise.all([
            offlineStore.getProjects(),
            offlineStore.getPendingCounts(),
            offlineStore.getSyncFailures(),
        ]);
        setProjects(p);
        setPending(counts);
        setFailures(fail);
        return p;
    }, []);

    useEffect(() => {
        loadData().then((p) => {
            const openId = searchParams.get("open");
            if (!openId) return;
            const match = p.find((proj) => proj.offlineId === openId);
            if (match) {
                setSelectedProject(match);
                setSearchParams({}, { replace: true });
            }
        });
    }, [loadData, searchParams, setSearchParams]);

    useEffect(() => {
        if (!selectedProject) return;
        const interval = setInterval(async () => {
            const realId = await offlineStore.getProjectMapping(
                selectedProject.offlineId,
            );
            if (realId && isOnline) {
                navigate(`/project/list/${realId}`);
            }
        }, 2000);
        return () => clearInterval(interval);
    }, [selectedProject, navigate, isOnline]);

    if (selectedProject) {
        return (
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    bgcolor: "background.surface",
                }}
            >
                <Box
                    sx={{
                        p: 2,
                        display: "flex",
                        alignItems: "center",
                        borderBottom: "1px solid",
                        borderColor: "divider",
                    }}
                >
                    <Button
                        variant="plain"
                        color="neutral"
                        startDecorator={<ChevronLeft size={20} />}
                        onClick={() => {
                            setSelectedProject(null);
                            loadData();
                        }}
                        sx={{ borderRadius: "4px" }}
                    >
                        Back to Offline Dashboard
                    </Button>
                    <Typography level="title-lg" sx={{ ml: 2 }}>
                        {selectedProject.payload.name || "Unnamed Project"} —
                        Inspection
                    </Typography>
                    <Chip size="sm" color="warning" variant="soft" sx={{ ml: "auto" }}>
                        Offline
                    </Chip>
                </Box>
                <Box sx={{ p: 3, flex: 1, overflow: "auto" }}>
                    <InitialInspection
                        activeTask={{
                            Id: `offline-task-${selectedProject.offlineId}`,
                            ProjectId: selectedProject.offlineId,
                            Name: "Initial Inspection",
                        }}
                        onComplete={() => {
                            setSelectedProject(null);
                            loadData();
                        }}
                    />
                </Box>
            </Box>
        );
    }

    const totalPending =
        pending.projects + pending.inspections + pending.photos;

    return (
        <Box sx={{ p: 4, maxWidth: 800, mx: "auto", width: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3, gap: 2 }}>
                <Box
                    sx={{
                        p: 1.5,
                        borderRadius: "4px",
                        bgcolor: "neutral.100",
                        color: "neutral.700",
                        border: "1px solid",
                        borderColor: "neutral.outlinedBorder",
                    }}
                >
                    <WifiOff size={24} />
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography level="title-lg">Offline Mode</Typography>
                    <Typography level="body-sm" color="neutral">
                        {isOnline
                            ? "You are back online. Pending items will sync automatically."
                            : "You are offline. Changes save locally and sync when reconnected."}
                    </Typography>
                </Box>
            </Box>

            {totalPending > 0 || failures.length > 0 ? (
                <Card
                    variant="soft"
                    color={failures.length ? "danger" : "primary"}
                    sx={{ p: 2, mb: 3, borderRadius: "4px" }}
                >
                    <Typography level="title-sm">
                        Pending sync: {pending.projects} project(s),{" "}
                        {pending.inspections} inspection(s), {pending.photos}{" "}
                        photo(s)
                    </Typography>
                    {failures.length > 0 ? (
                        <Typography level="body-xs" sx={{ mt: 0.5 }}>
                            {failures.length} recent failure(s). Retry from the
                            sync banner when online.
                        </Typography>
                    ) : null}
                    {isOnline ? (
                        <Typography level="body-xs" sx={{ mt: 0.5 }}>
                            Sync runs automatically while online
                        </Typography>
                    ) : null}
                </Card>
            ) : null}

            <Divider sx={{ mb: 4 }} />

            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 3,
                }}
            >
                <Typography level="title-md">Locally Saved Projects</Typography>
                <Button
                    startDecorator={<Plus size={16} />}
                    onClick={() => navigate("/project/new-quick?offline=true")}
                    sx={{ borderRadius: "4px" }}
                >
                    Create Quick Project
                </Button>
            </Box>

            {projects.length === 0 ? (
                <Card
                    variant="outlined"
                    sx={{
                        p: 4,
                        textAlign: "center",
                        bgcolor: "background.surface",
                        borderRadius: "4px",
                    }}
                >
                    <Typography level="body-md" color="neutral">
                        No offline projects created yet.
                    </Typography>
                </Card>
            ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    {projects.map((project) => (
                        <Card
                            key={project.offlineId}
                            variant="outlined"
                            sx={{ p: 3, borderRadius: "4px" }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                }}
                            >
                                <Box>
                                    <Typography level="title-md">
                                        {project.payload.name ||
                                            "Unnamed Project"}
                                    </Typography>
                                    <Typography level="body-sm" color="neutral">
                                        Type: {project.payload.type} • Saved
                                        locally
                                    </Typography>
                                </Box>
                                <Button
                                    variant="solid"
                                    color="primary"
                                    startDecorator={<FileText size={16} />}
                                    onClick={() => setSelectedProject(project)}
                                    sx={{ borderRadius: "4px" }}
                                >
                                    Inspection
                                </Button>
                            </Box>
                        </Card>
                    ))}
                </Box>
            )}
        </Box>
    );
}
